import { RefObject, useEffect, useRef } from 'react';

type ElementRef = RefObject<HTMLElement | null>;

export type UseOutsideClickParams = {
    /** the element, or several ones that count as inside (e.g. a field and its dropdown rendered in a portal) */
    ref: ElementRef | ReadonlyArray<ElementRef>;
    callback?: () => void;
    /** false: no listener (e.g. while the dropdown is closed) */
    isEnabled?: boolean;
    /** a scroll of the page calls back too (a menu that would stay at its old place) */
    closeOnScroll?: boolean;
    /** a touch outside calls back at once, not only the mousedown that a tap emits after it (a modal on phones) */
    closeOnTouchStart?: boolean;
    /**
     * Listen in the capture phase: a press on a component that stops its propagation (react-select stops the
     * mousedown of its arrow and menu) still counts. Off by default: then a portal inside the element that stops the
     * press (a react-select menu rendered in the body) does not close it
     */
    capture?: boolean;
};

/**
 * Calls back on a press (mousedown; a tap emits it too) outside the elements, and with closeOnScroll on a scroll of
 * the page. Nothing is called while none of them is mounted. The latest callback and refs are used, so inline ones do
 * not resubscribe the listener.
 */
export const useOutsideClick = ({
    ref,
    callback,
    isEnabled = true,
    closeOnScroll = false,
    closeOnTouchStart = false,
    capture = false
}: UseOutsideClickParams) => {
    const latestRef = useRef({ ref, callback });
    latestRef.current = { ref, callback };

    useEffect(() => {
        if (!isEnabled) {
            return undefined;
        }
        // A scroll of the page has the document as its target: outside every element
        const handleOutsideEvent = (event: Event) => {
            const { ref: currentRef, callback: currentCallback } = latestRef.current;
            const refs = 'current' in currentRef ? [currentRef] : currentRef;
            const elements = refs.map(({ current }) => current).filter((element): element is HTMLElement => !!element);
            const target = event.target as Node | null;

            if (elements.length && !elements.some(element => element.contains(target))) {
                currentCallback?.();
            }
        };

        // The scroll is never captured: only the page scroll counts, not the scroll of a list inside it
        document.addEventListener('mousedown', handleOutsideEvent, capture);
        if (closeOnScroll) {
            document.addEventListener('scroll', handleOutsideEvent);
        }
        if (closeOnTouchStart) {
            document.addEventListener('touchstart', handleOutsideEvent, capture);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideEvent, capture);
            document.removeEventListener('scroll', handleOutsideEvent);
            document.removeEventListener('touchstart', handleOutsideEvent, capture);
        };
    }, [isEnabled, closeOnScroll, closeOnTouchStart, capture]);
};
